/**
 * Choosing which table a dine-in order is for.
 *
 * WHY THE TILL ASKS AT ALL
 * ------------------------
 * Orders have carried a table number since the beginning and nothing ever
 * asked for one — 99 of 1,009 dine-in orders had a value in it, from
 * somewhere else entirely. A waiter could not be told which table an order
 * belonged to, which is most of what running a floor is.
 *
 * WHY OCCUPIED TABLES ARE SHOWN AND NOT HIDDEN
 * --------------------------------------------
 * A cashier looking for table 6 needs to see that 6 is taken and by whom,
 * rather than wonder why it has vanished from a list. Shown, greyed, and not
 * selectable.
 */
import { useQuery } from '@tanstack/react-query';
import { formatPKR } from '@foodrun/shared';

import { api } from '../../lib/api.js';
import { useCart } from './cart.js';

const AREAS = {
  main: 'Inside',
  outdoor: 'Outside',
  terrace: 'Terrace',
  private: 'Private room',
  counter: 'Counter',
};

export function TablePicker() {
  const orderType = useCart((s) => s.orderType);
  const tableId = useCart((s) => s.tableId);
  const setTable = useCart((s) => s.setTable);

  const floor = useQuery({
    queryKey: ['pos', 'floor'],
    queryFn: () => api.get('/pos/tables'),
    // Only while it could be asked for. A takeaway till should not be
    // fetching the floor every twenty seconds.
    enabled: orderType === 'dine_in',
    refetchInterval: 20_000,
  });

  if (orderType !== 'dine_in') return null;

  const tables = floor.data?.floor ?? [];
  if (tables.length === 0) return null;

  const byArea = tables.reduce((groups, table) => {
    (groups[table.area] ??= []).push(table);
    return groups;
  }, {});

  return (
    <div className="border-b border-rule p-3">
      <div className="mb-1.5 flex items-baseline justify-between">
        <span className="text-xs text-ink-3">Table</span>
        {tableId && (
          <button
            type="button"
            onClick={() => setTable(null, '')}
            className="text-xs text-ink-3 underline hover:text-ink"
          >
            Clear
          </button>
        )}
      </div>

      <div className="space-y-2">
        {Object.entries(byArea).map(([area, group]) => (
          <div key={area}>
            {Object.keys(byArea).length > 1 && (
              <span className="mb-1 block text-xs text-ink-3">{AREAS[area] ?? area}</span>
            )}

            <div className="flex flex-wrap gap-1.5">
              {group.map((table) => {
                const chosen = table.id === tableId;

                return (
                  <button
                    key={table.id}
                    type="button"
                    // Shown, greyed, not selectable — so a cashier can see
                    // that 6 is taken rather than wonder where it went.
                    disabled={table.occupied && !chosen}
                    onClick={() => setTable(table.id, table.number)}
                    title={
                      table.occupied && !chosen
                        ? `${table.order.number} — ${formatPKR(table.order.total)}`
                        : `${table.seats} seats`
                    }
                    className={`h-11 min-w-11 rounded-lg border px-2.5 text-sm ${
                      chosen
                        ? 'border-brand-400 bg-brand-400/10 text-ink'
                        : table.occupied
                          ? 'border-rule text-ink-3 opacity-50'
                          : 'border-rule text-ink-2 hover:border-brand-400 hover:text-ink'
                    }`}
                  >
                    {table.number}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
